/**
 * Pure helpers for the download progress dialog — transfer-rate and
 * estimated-time-remaining math, mirroring the legacy C++ `Ui_download_progress`
 * computation. Kept separate from the component so they can be unit-tested
 * without rendering.
 */

/** Transfer rate in bytes/sec for `bytes` received over an `elapsedSec` span. */
export function bytesPerSecond(bytes: number, elapsedSec: number): number {
  if (elapsedSec <= 0) return 0;
  return bytes / elapsedSec;
}

/** A timestamped cumulative-byte reading (ms epoch), for the windowed rate. */
export interface RateSample {
  t: number;
  bytes: number;
}

/** Default rate-averaging window (ms). Browsers show download speed as a moving
 *  average over a few seconds of recent history — Chromium's RateEstimator uses
 *  ~10 s (10 × 1 s buckets). We use a slightly shorter window so it stays
 *  responsive on the shorter data downloads here while still damping the
 *  second-to-second jitter of a raw instantaneous rate. */
export const RATE_WINDOW_MS = 5000;

/** Append `sample` to the rolling history and drop readings older than
 *  `windowMs` before it, bounding the history to the recent past. Returns a new
 *  trimmed array (oldest→newest); the caller stores it for the next reading. */
export function pushRateSample(
  history: RateSample[],
  sample: RateSample,
  windowMs = RATE_WINDOW_MS,
): RateSample[] {
  const cutoff = sample.t - windowMs;
  const trimmed = history.filter((s) => s.t >= cutoff);
  trimmed.push(sample);
  return trimmed;
}

/** Sliding-window transfer rate in bytes/sec, mirroring how browsers show
 *  download speed: a moving average over the last few seconds rather than a
 *  per-event instantaneous rate (which jitters) or a since-start average (which
 *  drifts up). Rate = bytes gained across the retained window ÷ its time span.
 *  Repeated identical readings (e.g. the web poll between backend updates) just
 *  extend the span, so the rate eases toward 0 on a real stall instead of
 *  spiking. */
export function windowedRate(samples: RateSample[]): number {
  if (samples.length < 2) return 0;
  const first = samples[0];
  const last = samples[samples.length - 1];
  return bytesPerSecond(last.bytes - first.bytes, (last.t - first.t) / 1000);
}

/** Seconds remaining to finish `total` bytes at `rate` bytes/sec (0 if unknown). */
export function etaSeconds(totalSize: number, currentSize: number, rateBytesPerSec: number): number {
  if (rateBytesPerSec <= 0) return 0;
  return Math.max(0, (totalSize - currentSize) / rateBytesPerSec);
}

/** Completion percentage clamped to 0..100 (0 when total is unknown). */
export function percentComplete(currentSize: number, totalSize: number): number {
  if (totalSize <= 0) return 0;
  return Math.min(100, Math.max(0, (currentSize / totalSize) * 100));
}

/** Format a byte/sec rate as "x.xx KB/s" (legacy dialog showed KB/s to 2 dp). */
export function formatRate(rateBytesPerSec: number): string {
  return `${(rateBytesPerSec / 1024).toFixed(2)} KB/s`;
}

/** Format a duration as "m min ss sec" (legacy `%2d min %02d sec`). */
export function formatEta(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const min = Math.floor(total / 60);
  const sec = total - min * 60;
  return `${min} min ${String(sec).padStart(2, "0")} sec`;
}

/** Format a byte count as a human-readable size (B / KB / MB). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
